import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetIsPreloadingData";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedPickUpDate";
import setShippingMethod from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetShippingMethod";
import translate from "@insite/client-framework/Translate";
import { CarrierDto, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import loadSystemList from "@tggstore/Store/Handlers/Custom/LoadSystemList";
import setTransactionType from "@tggstore/Store/Handlers/Custom/SetTransactionType";
import { CustomState } from "@tggstore/Store/Reducers";
import { TransactionTypeModel } from "@tggstore/Store/State/Custom/TransactionType";
// eslint-disable-next-line spire/fenced-imports
import { getUserProperties, setDefaultService, setDefaultTransactionType } from "@tggstore/Services/CommonServices";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomState) => {
    const settingsCollection = getSettingsCollection(state);
    const { session } = state.context;
    const { cartId } = state.pages.checkoutReviewAndSubmit;    
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
    const fulfillmentMethod = cart?.fulfillmentMethod || session.fulfillmentMethod;

    // Verndale Customization
    const billTo = getBillToState(state, cart?.billToId)?.value;
    const allowRM = billTo?.properties?.allowRM === "true";
    const defaultShipVia = cart?.shipVia || null;
    const defaultCarrier = cart?.carrier || null;
    const systemList = state.customSystemList.systemList;
    const { transactionType, showFormErrors } = state.customTransactionType;

    return {        
        cart,
        showShippingMethod: fulfillmentMethod === FulfillmentMethod.Ship,
        showPickUpDate:
            settingsCollection.accountSettings.enableWarehousePickup &&
            settingsCollection.cartSettings.enableRequestPickUpDate &&
            fulfillmentMethod === FulfillmentMethod.PickUp,
        session,
        accountSettings: settingsCollection.accountSettings,
        cartSettings: settingsCollection.cartSettings,
        allowEmptyShipping: settingsCollection.accountSettings.allowEmptyShipping,
        // Verndale Customization
        transactionType,
        allowRM,
        systemList,
        defaultShipVia,
        defaultCarrier,
        showFormErrors,
    };
};

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setIsPreloadingData,
    // Verndale Customization
    setTransactionType,
    loadSystemList,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface VerndaleCustomStyles {
    useDefaultCustomerCheckboxGroup?: CheckboxGroupProps;
    useDefaultCustomerCheckbox?: CheckboxPresentationProps;
}

export interface CheckoutShippingCarrierServiceStyles extends VerndaleCustomStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    carrierGridItem?: GridItemProps;
    carrierSelect?: SelectPresentationProps;
    serviceGridItem?: GridItemProps;
    serviceSelect?: SelectPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    carrierGridItem: { width: [6, 6, 6, 3, 3] },
    serviceGridItem: { width: [6, 6, 6, 3, 3] },
    deliveryDateGridItem: { width: [12, 12, 12, 6, 6] },
    pickUpDateGridItem: { width: [12, 12, 12, 6, 6] },
    deliveryDatePicker:{ // Verndale Customization Ticket: TGGOPT-1837
        cssOverrides:{
            descriptionWrapper:
                css`
                    font-style: italic;
                `,            
        },
    },
    pickUpDatePicker:{ // Verndale Customization Ticket: TGGOPT-1837
        cssOverrides:{
            descriptionWrapper:
                css`
                    font-style: italic;
                `,            
        },
    },
};

/**
 * @deprecated Use checkoutShippingCarrierServiceStyles instead.
 */
export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;

const CheckoutReviewAndSubmitCarrierService: FC<Props> = ({
    cart,
    cartSettings,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    showShippingMethod,
    setShippingMethod,
    showPickUpDate,
    setIsPreloadingData,
    allowEmptyShipping,
    // Verndale Customization
    transactionType,
    setTransactionType,
    allowRM,
    loadSystemList,
    systemList,
    defaultShipVia,
    showFormErrors,
    session,
}) => {
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null>(null);
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    const [useDefaultTransactionType, setUseDefaultTransaction] = useState(false);
    const [useDefaultService, setUseDefaultServices] = useState(false);

    const [defaultTransaction, setDefaultTransaction] = useState("");
    const [defaultService, setDefaultServices] = useState("");
    const [isDefaultTransaction, setIsDefaultTransaction] = useState("");
    const [isDefaultService, setIsDefaultService] = useState("");

    // Verndale Customization - Transaction Type added and set to 'qu' by default, also set by default to 'Will Call' ship via

    const [transactionTypeListValues, setTransactionTypeListValues] = useState<Array<TransactionTypeModel> | null>(
        null,
    );

    useEffect(() => {
        if (cart && cart.carriers && cart.carriers.length > 0 && !selectedCarrier && !selectedShipVia) {            
            const initialCarrier = cart.carriers[0];
            const updatedShipVias = initialCarrier.shipVias
                ? [
                      { id: "", description: translate("Select Service..."), isDefault: false },
                      ...initialCarrier.shipVias,
                  ]
                : [{ id: "", description: translate("Select Service..."), isDefault: false }];
            const updatedCarrier = { ...initialCarrier, shipVias: updatedShipVias };

            const initialShipVia =
            updatedCarrier.shipVias && updatedCarrier.shipVias.length > 0 ? updatedCarrier.shipVias[0] : null;

            setSelectedCarrier(updatedCarrier);

            if (sessionStorage.getItem("selectedShipVia")) {
                setSelectedShipVia(JSON.parse(sessionStorage.getItem("selectedShipVia")!));
            } else {
                setSelectedShipVia(initialShipVia);
            }
        }

        if (localStorage.getItem("requestedDeliveryDate")) {
            setRequestedDeliveryDate({
                requestedDeliveryDate: new Date(localStorage.getItem("requestedDeliveryDate")!),
                requestedDeliveryDateDisabled: false,
            });
        }
    }, [cart]);

    useEffect(() => {
        if (!systemList) {
            loadSystemList({ name: "TransactionType" });
        } else {
            const transactionTypeListValues = systemList.values
                .filter(value => {
                    if (!allowRM && value.name === "rm") {
                        return false;
                    }
                    return true;
                })
                .map(value => ({
                    id: value.name,
                    name: value.description,
                }));
            transactionTypeListValues.unshift({ id: "", name: translate("Select Transaction...") });

            setTransactionTypeListValues(transactionTypeListValues);
        }
    }, [systemList]);

    useEffect(() => {
        if (
            transactionTypeListValues &&
            transactionTypeListValues?.length >= 0 &&
            selectedCarrier &&
            selectedCarrier.shipVias &&
            selectedCarrier.shipVias.length > 0
        ) { 
            fetchUserProperties();
        }
    }, [transactionTypeListValues, selectedCarrier]);

    const fetchUserProperties = async () => {
        await getUserProperties(session.userProfileId!).then((data: any) => {
            const { defaultTransactionType, defaultService, isDefaultService, isDefaultTransaction } = data.result;

            setDefaultTransaction(defaultTransactionType);
            setDefaultServices(defaultService);
            setIsDefaultTransaction(isDefaultTransaction);
            setIsDefaultService(isDefaultService);

            if (isDefaultTransaction === "true" && defaultTransactionType !== "") {
                const transaction = transactionTypeListValues!.find(c => c.id === defaultTransactionType)!;
                setUseDefaultTransaction(true);
                setTransactionType({ transactionType: transaction });
            }
            if (isDefaultService === "true" && defaultService !== "" && selectedCarrier) {
                setUseDefaultServices(true);
                const selectedShipVia = selectedCarrier.shipVias?.find(s => s.id === defaultService) ?? null;
                if (selectedShipVia) {
                    sessionStorage.setItem("selectedShipVia", JSON.stringify(selectedShipVia));
                    setIsPreloadingData({ isPreloadingData: true });
                    setSelectedShipVia(selectedShipVia);
                    setShippingMethod({
                        carrier: selectedCarrier,
                        shipVia: selectedShipVia,
                        onComplete: () => {
                            setIsPreloadingData({ isPreloadingData: false });
                        },
                    });
                }
            }else if( sessionStorage.getItem("selectedShipVia") && selectedCarrier){
                const selectedShipVia = JSON.parse(sessionStorage.getItem("selectedShipVia")!);
                setShippingMethod({
                    carrier: selectedCarrier,
                    shipVia: selectedShipVia,
                    onComplete: () => {
                        setIsPreloadingData({ isPreloadingData: false });
                    },
                });
            }
        });
    };

    // End Verndale Customization

    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="checkoutShipping_carrierServiceLoading" />
            </StyledWrapper>
        );
    }

    const carrierChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        const selectedCarrier = cart.carriers!.find(c => c.id === event.currentTarget.value)!;
        const defaultShipVias = selectedCarrier.shipVias!.filter(o => o.isDefault);
        const shipVia = defaultShipVias.length > 0 ? defaultShipVias[0] : selectedCarrier.shipVias![0];
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedCarrier(selectedCarrier);
        setSelectedShipVia(shipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };

    // Verndale Customization
    const transactionTypeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        const transaction = transactionTypeListValues!.find(c => c.id === event.currentTarget.value)!;
        if (transaction.id === "") {
            setTransactionType({ transactionType: transaction });
            return;
        }
        if (defaultTransaction !== transaction.id) {
            setUseDefaultTransaction(false);
        } else if (isDefaultTransaction === "true") {
            setUseDefaultTransaction(true);
        } else {
            setUseDefaultTransaction(false);
        }

        setTransactionType({ transactionType: transaction });
    };

    // End Verndale Customization

    const shipViaChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        if (!selectedCarrier) {
            return;
        }
        const selectedShipVia = selectedCarrier.shipVias!.find(s => s.id === event.currentTarget.value)!;
        sessionStorage.setItem("selectedShipVia", JSON.stringify(selectedShipVia));
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedShipVia(selectedShipVia);
        if (defaultService !== selectedShipVia.id) {
            setUseDefaultServices(false);
        } else if (isDefaultService === "true") {
            setUseDefaultServices(true);
        } else {
            setUseDefaultServices(false);
        }

        setShippingMethod({
            carrier: selectedCarrier,
            shipVia: selectedShipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };

    const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
            requestedPickUpDateDisabled: selectedDayDisabled,
        });
    };
    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });

        if (selectedDay) {
            localStorage.setItem("requestedDeliveryDate", selectedDay!.toString());
        }
    };

    const useDefaultTransactionTypeHandler = async (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setIsPreloadingData({ isPreloadingData: true });
        setUseDefaultTransaction(value);
        await setDefaultTransactionType(transactionType.id, value ? "true" : "false").then(() => {
            fetchUserProperties();
            setIsPreloadingData({ isPreloadingData: false });
        });
    };

    const useDefaultServiceHandler = async (_: React.SyntheticEvent<Element, Event>, value: boolean) => {
        setIsPreloadingData({ isPreloadingData: true });
        setUseDefaultServices(value);
        if (selectedShipVia?.id) {
            await setDefaultService(selectedShipVia.id, value ? "true" : "false").then(() => {
                fetchUserProperties();
                setIsPreloadingData({ isPreloadingData: false });
            });
        }
    };

    return (
        <GridContainer {...styles.container}>
            {showShippingMethod && (
                <>
                    {cart.carriers!.length === 0 && !allowEmptyShipping && (
                        <GridItem {...styles.noCarriersFoundGridItem}>
                            <Typography {...styles.noCarriersFoundText}>
                                {siteMessage("ReviewAndPay_NoCarriersFound")}
                            </Typography>
                        </GridItem>
                    )}
                    {cart.carriers!.length > 0 && (
                        <>
                            <GridItem {...styles.carrierGridItem}>
                                {/* Verndale Customization: Hide Carrier Select */}
                                {/* <Select
                                    label={translate("Select Carrier")}
                                    {...styles.carrierSelect}
                                    value={selectedCarrier?.id || ""}
                                    onChange={carrierChangeHandler}
                                    data-test-selector="checkoutReviewAndSubmit_carrierSelect"
                                    name="carrier"
                                >
                                    {cart.carriers!.map(c => {
                                        const id = c.id!.toString();
                                        return (
                                            <option key={id} value={id}>
                                                {c.description}
                                            </option>
                                        );
                                    })}
                                </Select> */}

                                {/* Verndale Customziation: Display Transaction Tyoe Select */}

                                <Select
                                    label={translate("Transaction Type")}
                                    {...styles.carrierSelect}
                                    value={transactionType.id || ""}
                                    onChange={transactionTypeHandler}
                                    data-test-selector="checkoutReviewAndSubmit_transactionTypeSelect"
                                    name="transaction-type"
                                    required={true}
                                    error={
                                        showFormErrors &&
                                        transactionType.id === "" &&
                                        translate("Transaction type is required.")
                                    }
                                >
                                    {/* Verndale Custom Feature - New disabled option for placeholder */}
                                    {transactionTypeListValues?.map(c => {
                                        return (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        );
                                    })}
                                </Select>
                                {transactionType.id !== "" && (
                                    <CheckboxGroup {...styles.useDefaultCustomerCheckboxGroup}>
                                        <Checkbox
                                            {...styles.useDefaultCustomerCheckbox}
                                            aria-describedby="setDefault-transactionType"
                                            checked={useDefaultTransactionType}
                                            data-test-selector="setDefault-transactionType"
                                            onChange={useDefaultTransactionTypeHandler}
                                        >
                                            {siteMessage(
                                                "Set {0} as default?",
                                                transactionType.id === ""
                                                    ? ""
                                                    : `<strong>${transactionType.name}</strong>`,
                                            )}
                                        </Checkbox>
                                    </CheckboxGroup>
                                )}
                                {/* END Verndale Customization */}
                            </GridItem>
                            <GridItem {...styles.serviceGridItem}>
                                <Select
                                    label={translate("Select Service")}
                                    {...styles.serviceSelect}
                                    value={selectedShipVia?.id || ""}
                                    onChange={shipViaChangeHandler}
                                    data-test-selector="checkoutReviewAndSubmit_shippingServiceSelect"
                                    error={
                                        showFormErrors &&
                                        selectedShipVia?.id === "" &&
                                        translate("Service is required.")
                                    }
                                >
                                    {selectedCarrier &&
                                        selectedCarrier.shipVias!.map(s => {
                                            return (
                                                <option key={s.id} value={s.id}>
                                                    {s.description}
                                                </option>
                                            );
                                        })}
                                </Select>
                                {selectedShipVia?.id !== "" && (
                                    <CheckboxGroup {...styles.useDefaultCustomerCheckboxGroup}>
                                        <Checkbox
                                            {...styles.useDefaultCustomerCheckbox}
                                            aria-describedby="setDefault-service"
                                            checked={useDefaultService}
                                            data-test-selector="setDefault-service"
                                            onChange={useDefaultServiceHandler}
                                        >
                                            {siteMessage(
                                                "Set {0} as default?",
                                                selectedShipVia
                                                    ? `<strong>${selectedShipVia.description}</strong>`
                                                    : "",
                                            )}
                                        </Checkbox>
                                    </CheckboxGroup>
                                )}
                            </GridItem>
                        </>
                    )}
                    {cartSettings.canRequestDeliveryDate && (
                        <GridItem
                            {...styles.deliveryDateGridItem}
                            data-test-selector="checkoutShippingRequestedDeliveryDate"
                        >
                            <DatePicker
                                label={`${translate("Request Delivery Date")} (${translate("optional")})`}
                                hint={siteMessage("Checkout_RequestedDeliveryDateInformation")}
                                {...styles.deliveryDatePicker}
                                selectedDay={
                                    cart.requestedDeliveryDateDisplay
                                        ? cart.requestedDeliveryDateDisplay
                                        : localStorage.getItem("requestedDeliveryDate")
                                        ? new Date(localStorage.getItem("requestedDeliveryDate")!)
                                        : undefined
                                }
                                dateTimePickerProps={{
                                    minDate: new Date(),
                                    maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                    monthAriaLabel: translate("Request delivery date month"),
                                    dayAriaLabel: translate("Request delivery date day"),
                                    yearAriaLabel: translate("Request delivery date year"),
                                    ...styles.deliveryDatePicker?.dateTimePickerProps,
                                }}
                                onDayChange={handleRequestDeliveryDateChanged}
                            />
                        </GridItem>
                    )}
                </>
            )}
            {showPickUpDate && (
                <>
                    <GridItem {...styles.pickUpDateGridItem} data-test-selector="checkoutPickUpRequestedDeliveryDate">
                        <DatePicker
                            label={`${translate("Request Pick Up Date")} (${translate("optional")})`}
                            hint={siteMessage("Checkout_RequestedPickupDateInformation")}
                            {...styles.pickUpDatePicker}
                            selectedDay={cart.requestedPickupDateDisplay!}
                            dateTimePickerProps={{
                                minDate: new Date(),
                                maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                ...styles.pickUpDatePicker?.dateTimePickerProps,
                            }}
                            onDayChange={handleRequestPickUpDateChanged}
                            monthAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                "Enter month, format M M",
                            )}`}
                            dayAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                "Enter day, format D D",
                            )}`}
                            yearAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                "Enter year, format Y Y Y Y",
                            )}`}
                        />
                    </GridItem>
                </>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitCarrierService),
    definition: {
        displayName: "Carrier & Service",
        group: "Checkout - Review & Submit",
        allowedContexts: ["CheckoutReviewAndSubmitPage"],
    },
};

export default widgetModule;
